import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import DivStyle from "../../components/style/div.component";
import Table from "../../components/table/Table";
import Page from "../../components/table/paging";
import { FormProps } from "../../components/form/interface/formInterface";
import AdminAPI from "../../api/admin";
import ButtonStyle from "../../components/style/button.component";
import InputFormStyle from "../../components/style/input_form.component";
import Icon from "@mdi/react";
import { mdiMagnify } from "@mdi/js";
import SmallForm from "../../components/form/small_form";
import PosterManager from "./posterManager";
import {
  IRunnerCompetition,
  IRunnerCompetitionImage,
} from "./interface/interface";

const RunnerCompetition: React.FC = () => {
  const [brandCompetitionList, setBrandCompetitionList] = useState<
    IRunnerCompetition[]
  >([]);
  const [searchBrandCompetitionList, setSearchBrandCompetitionList] = useState<
    IRunnerCompetition[]
  >([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [mode, setMode] = useState("I");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [id, setId] = useState(0);
  const [title, setTitle] = useState("");
  const [startAt, setStartAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const [posterFiles, setPosterFiles] = useState<File[]>([]);
  const [sponsorFiles, setSponsorFiles] = useState<File[]>([]);
  const [tournamentIdStr, setTournamentIdStr] = useState<string>("");
  const [tournamentIds, setTournamentIds] = useState<string[]>([]);
  const [subTitle, setSubTitle] = useState("");
  const [currentThumbnailUrl, setCurrentThumbnailUrl] = useState<string>("");
  const [currentPosterUrls, setCurrentPosterUrls] = useState<string[]>([]);
  const [currentSponsorUrls, setCurrentSponsorUrls] = useState<string[]>([]);
  const [posterImages, setPosterImages] = useState<IRunnerCompetitionImage[]>(
    []
  );
  const [sponsorImages, setSponsorImages] = useState<IRunnerCompetitionImage[]>(
    []
  );

  const handleBasicFormAdd = async () => {
    await handleAdd();
  };

  const handleBasicFormSave = async () => {
    await handleSave();
  };

  const handleBasicFormRemove = async () => {
    await handleRemove();
  };

  const competitionFormData: FormProps = {
    title: "본사 대회 정보",
    form: [
      {
        value: title,
        setValue: setTitle,
        label: "타이틀",
        width: "100%",
        type: "input",
      },
      {
        value: subTitle,
        setValue: setSubTitle,
        label: "서브 타이틀",
        width: "50%",
        type: "input",
      },
      {
        value: selectedFile,
        setValue: setSelectedFile,
        label: "배너 이미지",
        width: "100%",
        type: "file",
        currentImageUrl: currentThumbnailUrl, // 이 부분 추가
      },
      {
        value: sponsorFiles,
        setValue: setSponsorFiles,
        label: "스폰서 이미지들",
        width: "100%",
        type: "files",
        multiple: true,
        currentImageUrls: currentSponsorUrls, // 이 부분 추가
      },
      {
        value: posterFiles,
        setValue: setPosterFiles,
        label: "포스터 이미지들",
        width: "100%",
        type: "files",
        multiple: true,
        currentImageUrls: currentPosterUrls, // 이 부분 추가
      },
      {
        value: startAt,
        setValue: setStartAt,
        label: "시작일시",
        width: "100%",
        type: "date",
      },
      {
        value: endAt,
        setValue: setEndAt,
        label: "종료일시",
        width: "100%",
        type: "date",
      },
      {
        value: tournamentIdStr,
        setValue: setTournamentIdStr,
        label: "연관 토너먼트 id (0000, 0000, ..)",
        width: "100%",
        type: "input",
      },
    ],
    add: handleBasicFormAdd,
    save: handleBasicFormSave,
    remove: handleBasicFormRemove,
    mode: mode,
  };

  useEffect(() => {
    const arr = tournamentIdStr
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    setTournamentIds(arr);
  }, [tournamentIdStr]);

  const headers = [
    {
      text: "ID",
      align: "center",
      sortable: false,
      value: "id",
    },
    {
      text: "타이틀",
      align: "center",
      sortable: false,
      value: "title",
    },
    {
      text: "서브 타이틀",
      align: "center",
      sortable: false,
      value: "subTitle",
    },
    {
      text: "시작일시",
      align: "center",
      sortable: false,
      value: "startDate",
    },
    {
      text: "종료일시",
      align: "center",
      sortable: false,
      value: "endDate",
    },
  ];

  const formatDate = (dateString: string): string => {
    if (!dateString) {
      return "Invalid Date";
    }

    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const seconds = date.getSeconds().toString().padStart(2, "0");

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const getList = async () => {
    try {
      const res = await AdminAPI.getRunnerCompetitions();
      if (res && res.status === 200) {
        const updatedData = res.data.list.map((item: IRunnerCompetition) => ({
          ...item,
          startDate: formatDate(item.startDate),
          endDate: formatDate(item.endDate),
        }));
        const totalPages = Math.ceil(res.total / 10);
        setTotalPages(totalPages);
        setBrandCompetitionList(updatedData);
        setSearchBrandCompetitionList(updatedData);
      }
    } catch (error) {
      console.error("Failed to fetch competitions:", error);
    }
  };

  useEffect(() => {
    getList();
  }, []);

  const handleAdd = async () => {
    if (!title) {
      alert("타이틀을 입력해주세요.");
      return;
    }
    if (!subTitle) {
      alert("서브타이틀을 입력해주세요.");
      return;
    }
    if (!selectedFile) {
      alert("썸네일 이미지를 선택해주세요.");
      return;
    }

    console.log(tournamentIds);

    const request = {
      title,
      subTitle,
      startDate: startAt,
      endDate: endAt,
      tournamentIds,
    };

    const formData = new FormData();

    // 파일 데이터 추가
    formData.append("banner", selectedFile);

    if (posterFiles) {
      Array.from(posterFiles).forEach((file) => {
        formData.append("posters", file);
      });
    }

    if (sponsorFiles) {
      Array.from(sponsorFiles).forEach((file) => {
        formData.append("sponsors", file);
      });
    }

    // JSON 데이터를 문자열로 변환하여 추가
    formData.append("data", JSON.stringify(request));

    if (window.confirm("신규등록 하시겠습니까?")) {
      try {
        const res = await AdminAPI.registerRunnerCompetition(formData);
        if (res?.status === 200 || res?.status === 201) {
          alert("신규 등록 되었습니다.");
          await getList();
          addForm();
        }
      } catch (error) {
        console.error("Registration failed:", error);

        // 에러 타입 가드 추가
        if (error instanceof AxiosError) {
          if (error.response) {
            console.error("Error details:", error.response.data);
            alert(error.response.data.message || "등록에 실패했습니다.");
          } else {
            alert("서버 연결에 실패했습니다.");
          }
        } else {
          alert("등록에 실패했습니다.");
        }
      }
    }
  };

  const addForm = () => {
    setMode("I");
    setId(0);
    setTitle("");
    setStartAt("");
    setEndAt("");
    setSelectedFile(null);
    setPosterFiles([]);
    setSubTitle("");
    setTournamentIds([]);
    setTournamentIdStr("");
    setCurrentThumbnailUrl("");
    setCurrentPosterUrls([]);
    setCurrentSponsorUrls([]);
    setPosterImages([]);
    setSponsorImages([]);
    setSponsorFiles([]);
  };

  const setDetail = async (item: IRunnerCompetition) => {
    // 상세 정보 조회
    setMode("U");
    setId(item.id);
    setTitle(item.title);
    setSubTitle(item.subTitle);
    setStartAt(formatDate(item.startDate));
    setEndAt(formatDate(item.endDate));
    setTournamentIds(item.tournamentIds || []);
    const str = item.tournamentIds.join(", ");
    setTournamentIdStr(str);
    setCurrentThumbnailUrl(item.bannerImage || "");
    const poster = item.posterImages.map((item) => item.imageUrl);
    const sponsor = item.sponsorImages.map((item) => item.imageUrl);
    setCurrentPosterUrls(poster || []);
    setCurrentSponsorUrls(sponsor || []);
    setPosterImages(item.posterImages);
    setSponsorImages(item.sponsorImages);
  };

  const handleSave = async () => {
    if (id > 0) {
      const request = {
        title,
        subTitle,
        tournamentIds,
        startDate: startAt,
        endDate: endAt,
        location: subTitle,
      };
      const formData = new FormData();

      // 파일 데이터 추가
      if (selectedFile) {
        formData.append("banner", selectedFile);
      }
      if (posterFiles) {
        Array.from(posterFiles).forEach((file) => {
          formData.append("posters", file);
        });
      }
      if (sponsorFiles) {
        Array.from(sponsorFiles).forEach((file) => {
          formData.append("sponsors", file);
        });
      }

      // JSON 데이터를 문자열로 변환하여 추가
      formData.append("data", JSON.stringify(request));

      if (window.confirm("수정 하시겠습니까?")) {
        try {
          const res = await AdminAPI.updateRunnerCompetition(id, formData);
          if (res?.status === 200) {
            alert("수정되었습니다.");
            await getList();
            addForm();
          }
        } catch (error) {
          // ... 에러 처리
        }
      }
    }
  };

  const handleRemove = async () => {
    if (window.confirm("삭제 하시겠습니까?")) {
      if (id) {
        try {
          const res = await AdminAPI.deleteRunnerCompetition(id);
          if (res?.status === 200) {
            alert("삭제되었습니다.");
            await getList();
            addForm();
          }
        } catch (error) {
          alert("삭제에 실패했습니다.");
          console.error("Delete failed:", error);
        }
      }
    }
  };

  const handleSearch = (search: string) => {
    setCurrentPage(1);

    if (!search.trim()) {
      setSearchBrandCompetitionList(brandCompetitionList);
      const totalPages = Math.ceil(brandCompetitionList.length / 10);
      setTotalPages(totalPages);
      return;
    }

    const filteredList = brandCompetitionList.filter(
      (item: IRunnerCompetition) => {
        return Object.values(item).some((value) => {
          if (value) {
            return value
              .toString()
              .toLowerCase()
              .includes(search.toLowerCase());
          }
          return false;
        });
      }
    );

    setSearchBrandCompetitionList(filteredList);
    const totalPages = Math.ceil(filteredList.length / 10);
    setTotalPages(totalPages);
  };

  return (
    <DivStyle
      style={{ overflow: "auto" }}
      backgroundColor="#121212"
      height="100vh"
      display="flex"
      flex_direction="row"
      marginTop="65px"
      setMobile={true}
    >
      {/* 왼쪽 영역: 대회 목록 */}
      <DivStyle
        width="66%"
        height="auto"
        display="flex"
        flex_direction="column"
        align_items="center"
        setMobileTable={true}
      >
        <h2 style={{ color: "white", width: "auto", marginTop: "40px" }}>
          본사 대회 관리
        </h2>
        <DivStyle
          width="95%"
          display="flex"
          backgroundColor="#1E1E1E"
          flex_direction="column"
          borderRadius="10px"
          marginTop="30px"
          marginBottom="10px"
        >
          <DivStyle
            width="100%"
            display="flex"
            flex_direction="row"
            justify_content="end"
            align_items="center"
          >
            <DivStyle
              width="50%"
              display="flex"
              justify_content="flex-end"
              flex_direction="row"
              align_items="center"
              marginRight="20px"
              marginTop="20px"
            >
              <InputFormStyle onChange={(e) => handleSearch(e.target.value)} />
              <Icon path={mdiMagnify} size={1} style={{ color: "white" }} />
            </DivStyle>
          </DivStyle>
          <Table
            headers={headers}
            currentPage={currentPage}
            dataList={searchBrandCompetitionList}
            setDetail={setDetail}
          />
          <Page
            currentPage={currentPage}
            totalPages={totalPages}
            dataList={searchBrandCompetitionList}
            setCurrentPage={setCurrentPage}
          />
        </DivStyle>
        <DivStyle
          marginBottom="50px"
          width="95%"
          display="flex"
          justify_content="flex-end"
        >
          <ButtonStyle
            border="none"
            backgroundColor="#1976D2"
            width="85px"
            height="40px"
            color="white"
            borderRadius="5px"
            onClick={addForm}
          >
            신규 등록
          </ButtonStyle>
        </DivStyle>
      </DivStyle>

      {/* 오른쪽 영역: 입력 폼 */}
      <DivStyle
        width="33%"
        height="auto"
        display="flex"
        flex_direction="column"
        align_items="center"
        setMobileForm={true}
      >
        <h2 style={{ color: "white", width: "auto", marginTop: "40px" }}>
          대회 정보
        </h2>

        {/* 기본 정보 폼 */}
        <DivStyle
          width="95%"
          display="flex"
          backgroundColor="#1E1E1E"
          flex_direction="column"
          borderRadius="10px"
          marginTop="30px"
          marginBottom="30px"
        >
          <SmallForm {...competitionFormData} />
        </DivStyle>
        {currentSponsorUrls.length > 0 && (
          <PosterManager
            posters={sponsorImages}
            setPosters={setSponsorImages}
            getData={getList}
            type="sponsor"
          />
        )}
        {currentPosterUrls.length > 0 && (
          <PosterManager
            posters={posterImages}
            setPosters={setPosterImages}
            getData={getList}
            type="poster"
          />
        )}
      </DivStyle>
    </DivStyle>
  );
};

export default RunnerCompetition;
